/**
 * DialogCoordinator - Dialog lifecycle management
 *
 * Handles opening, closing, and state management for dialogs.
 * Coordinates with DialogStore and preview state.
 */

import { AppStore } from '../stores/AppStore';
import { DialogStore } from '../stores/DialogStore';
import { DialogName } from '../types';
import { DIALOG_REGISTRY } from '../dialog-registry';
import { syncDialogToUrl, clearDialogFromUrl } from './UrlStateSync';
import { setColumnEditorSection } from '../handlers/dialog/column-editor-handlers';

export type DialogCallbacks = {
  clearColumnSelection?: () => void;
  confirm?: (message: string, confirmLabel?: string) => Promise<boolean>;
};

let callbacks: DialogCallbacks | null = null;

/**
 * Set callbacks for dialog coordination
 */
export function setDialogCallbacks(cb: DialogCallbacks): void {
  callbacks = cb;
}

/**
 * Get serializable state for a dialog (used for change detection).
 * Delegates to the dialog registry's getState function.
 */
export function getDialogState(dialog: string): any {
  return DIALOG_REGISTRY[dialog]?.getState?.() ?? null;
}

/**
 * Take a snapshot of current dialog state (for change detection)
 */
export function snapshotDialogState(): void {
  const dialog = AppStore.activeDialog.value;
  if (dialog) {
    AppStore.dialogSnapshot.value = JSON.stringify(getDialogState(dialog));
  }
}

/**
 * Check if dialog has unsaved changes
 */
export function hasUnsavedChanges(): boolean {
  const dialog = AppStore.activeDialog.value;
  const snapshot = AppStore.dialogSnapshot.value;
  if (!dialog || snapshot === null) return false;

  const current = getDialogState(dialog);
  if (current === null) return false;

  return JSON.stringify(current) !== snapshot;
}

/**
 * Initialize state for a specific dialog
 */
export function initDialogState(dialogName: string, section?: string): void {
  // Settings dialog: initialize from AppStore (non-transform, no useDialogState)
  if (dialogName === 'settings') {
    const state = DialogStore.settingsState;
    state.theme.value = AppStore.theme.value as any;
    state.rowLimit.value = AppStore.uxSettings.value.preview?.rowLimit || 100;
    state.analyticsOptOut.value = AppStore.uxSettings.value.analyticsOptOut ?? false;
    state.language.value = AppStore.uxSettings.value.language || 'en';
    state.engine.value = AppStore.uxSettings.value.experimental?.engine || 'arquero';
    return;
  }

  // Column-editor section preset (useDialogState reads AppStore, but section needs forwarding)
  if (dialogName === 'column-editor' && section) {
    setColumnEditorSection(section);
    return;
  }

  // All other dialogs: delegate to registry initState if present
  const config = DIALOG_REGISTRY[dialogName];
  config?.initState?.(section);
}

/**
 * Open a dialog
 */
export function openDialog(dialogName: string, section?: string): void {
  AppStore.activeDialog.value = dialogName as DialogName;
  initDialogState(dialogName, section);
  callbacks?.clearColumnSelection?.();
  snapshotDialogState();

  // Update URL for navigable pages
  syncDialogToUrl(dialogName, section);
}

/**
 * Close the current dialog
 */
export async function closeDialog(force = false): Promise<void> {
  const dialog = AppStore.activeDialog.value;

  if (!force && hasUnsavedChanges()) {
    const confirmed = await callbacks?.confirm?.(
      'You have unsaved changes. Are you sure you want to discard them?'
    );
    if (!confirmed) return;
  }

  // Clear preview state
  clearPreview();

  // Clear URL if closing a navigable dialog
  if (dialog) {
    clearDialogFromUrl(dialog);
  }

  AppStore.activeDialog.value = null;
  AppStore.dialogSnapshot.value = null;
  DialogStore.resetAll();
  AppStore.importFileData.value = null;
}

/**
 * Clear preview state
 */
export function clearPreview(): void {
  DialogStore.previewState.title.value = '';
  DialogStore.previewState.stats.value = '';
  DialogStore.previewState.columns.value = [];
  DialogStore.previewState.newColumns.value = [];
  DialogStore.previewState.rows.value = [];
}

/**
 * Check if current dialog has an error that should disable the apply button.
 * Delegates to the dialog registry's hasError function.
 */
export function activeDialogHasError(): boolean {
  const dialog = AppStore.activeDialog.value;
  if (!dialog) return false;
  return DIALOG_REGISTRY[dialog]?.hasError?.() ?? false;
}

/**
 * Get the error message for the current dialog's disabled Apply button.
 * Returns null if no error or if the dialog doesn't expose error messages.
 */
export function getActiveDialogError(): string | null {
  const dialog = AppStore.activeDialog.value;
  if (!dialog) return null;
  return DIALOG_REGISTRY[dialog]?.getError?.() ?? null;
}

/**
 * Check if current dialog has preview data
 */
export function hasPreviewData(): boolean {
  const dialog = AppStore.activeDialog.value;

  if (dialog === 'import-csv') {
    return DialogStore.importCsvState.previewDataRows.value.length > 0;
  }

  return DialogStore.previewState.rows.value.length > 0;
}

/**
 * Get preview title for current dialog
 */
export function getPreviewTitle(): string {
  const dialog = AppStore.activeDialog.value;

  if (dialog === 'import-csv') {
    return 'Import Preview';
  }

  return DialogStore.previewState.title.value;
}

/**
 * Get preview stats for current dialog
 */
export function getPreviewStats(): string {
  const dialog = AppStore.activeDialog.value;

  if (dialog === 'import-csv') {
    const rows = DialogStore.importCsvState.previewDataRows.value.length;
    const cols = DialogStore.importCsvState.previewHeaders.value.length;
    const limit = AppStore.uxSettings.value.preview.rowLimit;
    return `${rows} rows, ${cols} columns (first ${Math.min(rows, limit)} rows shown)`;
  }

  return DialogStore.previewState.stats.value;
}

/**
 * Get preview columns for current dialog
 */
export function getPreviewColumns(): string[] {
  const dialog = AppStore.activeDialog.value;

  if (dialog === 'import-csv') {
    return DialogStore.importCsvState.previewHeaders.value;
  }

  return DialogStore.previewState.columns.value;
}

/**
 * Get preview rows for current dialog
 */
export function getPreviewRows(): any[] {
  const dialog = AppStore.activeDialog.value;

  if (dialog === 'import-csv') {
    const headers = DialogStore.importCsvState.previewHeaders.value;
    const rows = DialogStore.importCsvState.previewDataRows.value;
    return rows.map((row: any[]) => {
      const obj: any = {};
      headers.forEach((header, i) => {
        obj[header] = row[i];
      });
      return obj;
    });
  }

  return DialogStore.previewState.rows.value;
}

/**
 * Check if a column is new in the preview
 */
export function isNewPreviewColumn(col: string): boolean {
  return DialogStore.previewState.newColumns.value.includes(col);
}
